// Lightweight canvas charts (HiDPI): curves, bars, workout profile, power curve, fitness/fatigue.
import { flatten, blockFrac, zoneOf, maxOf } from '@gruvetto/core';
import { effortLabel, courseSamples, type Track } from '@gruvetto/core';
import { t, fmtDayMonth, fmtNumber } from '../../i18n/index.js';

const css = v => getComputedStyle(document.documentElement).getPropertyValue(v).trim() || '#888';
/** Same, read where the canvas sits: the ride HUD keeps dark colours whatever the interface theme. */
/** Colour of a data series (tokens --c-fitness, --c-fatigue…), so canvas lines match their CSS legends. */
export const seriesColor = (name: 'fitness' | 'fatigue' | 'sprint' | 'climb' | 'power'): string => (name === 'power' ? css('--acc') : css('--c-' + name));
const cssAt = (el: Element, v: string) => getComputedStyle(el).getPropertyValue(v).trim() || '#888';

function setup(canvas, h) {
  const dpr = Math.min(2, devicePixelRatio || 1), w = canvas.clientWidth || canvas.parentElement?.clientWidth || 600;
  h = h || canvas.clientHeight || 160;
  canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
  canvas.style.height = h + 'px';
  const g = canvas.getContext('2d'); g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h);
  g.font = '500 11px "Inter Variable", Inter, system-ui, sans-serif';
  return { g, w, h };
}
const niceMax = v => { if (v <= 0) return 1; const p = 10 ** Math.floor(Math.log10(v)), n = v / p; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p; };

function grid(g, w, h, pad, max, fmt = v => v) {
  g.fillStyle = css('--mut2'); g.lineWidth = 1; g.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const y = Math.round(pad.t + ((h - pad.t - pad.b) * i) / 4) + 0.5, v = max * (1 - i / 4);
    g.strokeStyle = `rgba(${css('--tint')},${i === 4 ? '.12' : '.05'})`; g.setLineDash(i === 4 ? [] : [3, 4]);
    g.beginPath(); g.moveTo(pad.l, y); g.lineTo(w - pad.r, y); g.stroke();
    // Ticks at the tenth if the scale is small (avoids "2, 2, 1, 1, 0").
    g.fillText(fmt(fmtNumber(Number.isInteger(v) ? v : +v.toFixed(1))), pad.l - 8, y + 4); // tick in the locale's format ("0,5")
  }
  g.setLineDash([]);
}

/**
 * Time curves. series: [{ data: number[], color, fill?, label, axis?: 'right', max? }], xs: x values (s).
 */
export function lineChart(canvas, xs, series, { h = 200, xfmt = v => v, yfmt = v => v }: any = {}) {
  const { g, w } = setup(canvas, h), pad = { l: 42, r: series.some(s => s.axis === 'right') ? 40 : 12, t: 10, b: 22 };
  if (!xs.length) return;
  const left = series.filter(s => s.axis !== 'right'), maxL = niceMax(Math.max(1, ...left.map(s => maxOf(s.data))) * 1.05); // a series can outgrow an argument list
  grid(g, w, h, pad, maxL, yfmt);
  const x0 = xs[0], x1 = xs[xs.length - 1] || 1, X = x => pad.l + ((x - x0) / (x1 - x0 || 1)) * (w - pad.l - pad.r);
  g.textAlign = 'center'; g.fillStyle = css('--mut');
  for (let i = 0; i <= 5; i++) { const x = x0 + ((x1 - x0) * i) / 5; g.textAlign = i === 0 ? 'left' : i === 5 ? 'right' : 'center'; g.fillText(xfmt(x), X(x), h - 6); }
  const step = Math.max(1, Math.floor(xs.length / (w * 1.5)));
  for (const s of series) {
    const max = s.axis === 'right' ? niceMax(maxOf(s.data, 1) * 1.05) : maxL, Y = v => pad.t + (1 - v / max) * (h - pad.t - pad.b);
    g.beginPath();
    for (let i = 0; i < xs.length; i += step) { const x = X(xs[i]), y = Y(s.data[i] || 0); if (i) g.lineTo(x, y); else g.moveTo(x, y); }
    if (s.fill) { g.lineTo(X(xs[xs.length - 1]), h - pad.b); g.lineTo(X(xs[0]), h - pad.b); g.closePath(); g.fillStyle = s.fill; g.fill(); }
    else { g.strokeStyle = s.color; g.lineWidth = s.width || 1.6; g.stroke(); }
    if (s.axis === 'right') { g.fillStyle = s.color; g.textAlign = 'left'; g.fillText(Math.round(max), w - pad.r + 6, pad.t + 4); g.fillText('0', w - pad.r + 6, h - pad.b); }
  }
}

/** Simple histogram. bars: [{ label, value, color? }] */
export function barChart(canvas, bars, { h = 180, yfmt = v => v, color = css('--acc') } = {}) {
  const { g, w } = setup(canvas, h), pad = { l: 42, r: 10, t: 10, b: 22 };
  const max = niceMax(Math.max(1, ...bars.map(b => b.value)) * 1.1);
  grid(g, w, h, pad, max, yfmt);
  const bw = (w - pad.l - pad.r) / bars.length;
  bars.forEach((b, i) => {
    const bh = (b.value / max) * (h - pad.t - pad.b), x = pad.l + i * bw + bw * 0.18, y = h - pad.b - bh;
    const gr = g.createLinearGradient(0, y, 0, h - pad.b); gr.addColorStop(0, b.color || color); gr.addColorStop(1, 'rgba(255,159,28,.35)');
    g.fillStyle = gr; g.beginPath(); g.roundRect(x, y, bw * 0.64, Math.max(1, bh), 4); g.fill();
    g.fillStyle = css('--mut'); g.textAlign = 'center'; g.fillText(b.label, x + bw * 0.32, h - 6);
  });
}

/** Coloured workout profile (blocks per zone). progress: elapsed time (s) for the cursor. */
export function workoutProfile(canvas, workout, ftp, { h = 90, progress = null, blocks = null }: any = {}) {
  const { g, w } = setup(canvas, h);
  blocks = blocks || flatten(workout);
  const total = blocks.reduce((a, b) => a + b.dur, 0) || 1;
  const maxF = Math.max(1.3, ...blocks.map(b => (b.free ? 1 : Math.max(b.p1 ?? 0, b.p0 ?? 0, (b.w1 ?? 0) / ftp, (b.w0 ?? 0) / ftp))));
  const Y = f => h - 4 - (f / maxF) * (h - 10);
  for (const b of blocks) {
    const x = (b.start / total) * w, bw = Math.max(1, (b.dur / total) * w);
    const f0 = b.free ? 0.6 : b.w0 != null ? b.w0 / ftp : b.p0, f1 = b.free ? 0.6 : b.w1 != null ? b.w1 / ftp : b.p1;
    const c = b.free ? '#6b7280' : zoneOf(blockFrac(b, ftp)).color;
    const past = progress != null && b.start + b.dur <= progress;
    g.fillStyle = c; g.globalAlpha = progress == null ? 0.9 : past ? 0.35 : 0.95;
    g.beginPath(); g.moveTo(x, h); g.lineTo(x, Y(f0)); g.lineTo(x + bw, Y(f1)); g.lineTo(x + bw, h); g.closePath(); g.fill();
  }
  g.globalAlpha = 1;
  g.strokeStyle = `rgba(${cssAt(canvas, '--tint')},.35)`; g.setLineDash([4, 4]); g.beginPath(); g.moveTo(0, Y(1)); g.lineTo(w, Y(1)); g.stroke(); g.setLineDash([]);
  if (progress != null) { const x = (progress / total) * w; g.fillStyle = cssAt(canvas, '--txt'); g.fillRect(x - 1.5, 0, 3, h); }
}

/** Power curve on a logarithmic scale. curves: [{ data: {dur: W}, color, label }] */
export function powerCurveChart(canvas, curves, { h = 200 }: any = {}) {
  const { g, w } = setup(canvas, h), pad = { l: 42, r: 12, t: 10, b: 22 };
  const durs = Object.keys(curves[0]?.data || {}).map(Number).sort((a, b) => a - b);
  if (!durs.length) return;
  const max = niceMax(Math.max(1, ...curves.map(c => maxOf(Object.values(c.data) as number[]))) * 1.05);
  grid(g, w, h, pad, max);
  const lx = d => Math.log(d), X = d => pad.l + ((lx(d) - lx(durs[0])) / (lx(durs.at(-1)) - lx(durs[0]))) * (w - pad.l - pad.r), Y = v => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  g.textAlign = 'center'; g.fillStyle = css('--mut');
  for (const d of durs) g.fillText(effortLabel(d), X(d), h - 6);
  for (const c of curves) {
    const pts = durs.filter(d => c.data[d] > 0);
    g.strokeStyle = c.color; g.lineWidth = 2.2; g.beginPath();
    pts.forEach((d, i) => (i ? g.lineTo(X(d), Y(c.data[d])) : g.moveTo(X(d), Y(c.data[d]))));
    g.stroke();
    g.fillStyle = c.color;
    for (const d of pts) { g.beginPath(); g.arc(X(d), Y(c.data[d]), 3, 0, 7); g.fill(); }
  }
}

/** Fitness (CTL), fatigue (ATL), freshness (TSB). */
export function pmcChart(canvas, series, { h = 200 }: any = {}) {
  const xs = series.map((_, i) => i);
  lineChart(canvas, xs, [
    { data: series.map(d => d.tss), fill: 'rgba(255,212,0,.12)' },
    { data: series.map(d => d.ctl), color: seriesColor('fitness'), width: 2.4 },
    { data: series.map(d => d.atl), color: seriesColor('fatigue'), width: 1.6 },
  ], { h, xfmt: i => { const d = series[Math.round(i)]; return d ? fmtDayMonth(d.day) : ''; } });
}

/** Track: layout seen from above (left) and elevation profile over one lap, or start to finish on an open road (right). */
export function trackPreview(canvas, tr: Track, { h = 180 }: any = {}) {
  const { g, w } = setup(canvas, h), mapW = Math.min(h * 1.2, w * 0.42), pad = 12, n = courseSamples(tr);
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (let i = 0; i < n; i++) { x0 = Math.min(x0, tr.x[i]); x1 = Math.max(x1, tr.x[i]); z0 = Math.min(z0, tr.z[i]); z1 = Math.max(z1, tr.z[i]); }
  const k = Math.min((mapW - pad * 2) / (x1 - x0 || 1), (h - pad * 2) / (z1 - z0 || 1));
  const X = x => pad + (x - x0) * k + (mapW - pad * 2 - (x1 - x0) * k) / 2, Z = z => pad + (z - z0) * k + (h - pad * 2 - (z1 - z0) * k) / 2;
  const step = Math.max(1, Math.floor(n / 600));
  g.lineCap = 'round';
  for (let i = 0; i < (tr.open ? n - 1 : n); i += step) {
    const j = tr.open ? Math.min(n - 1, i + step) : (i + step) % n, gr = tr.grade[i];
    g.strokeStyle = gr > 0.06 ? css('--bad') : gr > 0.03 ? css('--warn') : gr < -0.03 ? seriesColor('fitness') : css('--acc');
    g.lineWidth = 4; g.beginPath(); g.moveTo(X(tr.x[i]), Z(tr.z[i])); g.lineTo(X(tr.x[j]), Z(tr.z[j])); g.stroke();
  }
  g.fillStyle = cssAt(canvas, '--txt'); g.beginPath(); g.arc(X(tr.x[0]), Z(tr.z[0]), 5, 0, 7); g.fill();
  // an open road: the finish is a second, hollow mark
  if (tr.open) { g.strokeStyle = cssAt(canvas, '--txt'); g.lineWidth = 2.5; g.beginPath(); g.arc(X(tr.x[n - 1]), Z(tr.z[n - 1]), 5, 0, 7); g.stroke(); }
  const px = mapW + 16, pw = w - px - 8;
  let y0 = Infinity, y1 = -Infinity;
  for (let i = 0; i < n; i++) { y0 = Math.min(y0, tr.y[i]); y1 = Math.max(y1, tr.y[i]); }
  const span = Math.max(20, y1 - y0), Y = y => h - 20 - ((y - y0) / span) * (h - 40);
  g.beginPath(); g.moveTo(px, h - 20);
  for (let i = 0; i < n; i += step) g.lineTo(px + (i / n) * pw, Y(tr.y[i]));
  g.lineTo(px + pw, h - 20); g.closePath();
  const grad = g.createLinearGradient(0, 20, 0, h - 20); grad.addColorStop(0, 'rgba(255,212,0,.55)'); grad.addColorStop(1, 'rgba(255,212,0,.05)');
  g.fillStyle = grad; g.fill();
  g.fillStyle = css('--mut'); g.textAlign = 'left';
  g.fillText(t.charts.elevationRange(Math.round(y1 - y0), (tr.lap / 1000).toFixed(2)), px, h - 4);
}
